import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { listFrameworks } from "@/api/frameworks";
import { listRuns } from "@/api/runs";
import { generationErrorMessage } from "@/api/llm-access";
import { IN_FLIGHT_STATUSES, type RunRead } from "@/api/create-types";
import { presentationFor } from "./framework-config";
import { usePendingRuns, dismissRun } from "./active-runs-store";
import { ContinueLearning } from "./continue-learning";
import { Alert, Spinner } from "@/components/ui";

export function CreateGallery() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["frameworks"],
    queryFn: listFrameworks,
  });

  // In-progress runs — so the user can return to a paused marking session.
  const runsQuery = useQuery({
    queryKey: ["active-runs"],
    queryFn: listRuns,
    refetchInterval: (q) => {
      const d = q.state.data as RunRead[] | undefined;
      return d && d.some((r) => IN_FLIGHT_STATUSES.includes(r.status)) ? 4000 : false;
    },
  });
  const activeRuns = (runsQuery.data ?? []).filter((r) => IN_FLIGHT_STATUSES.includes(r.status));
  // Optimistic placeholders for runs whose synchronous POST is still in flight
  // (not yet committed, so invisible to listRuns). These give the user instant
  // "it started" feedback the moment they land here from the Generate button.
  const pendingRuns = usePendingRuns();
  const titleFor = (slug: string) => data?.find((f) => f.slug === slug)?.title ?? slug;

  return (
    <div className="animate-fade-up">
      <h1 className="text-3xl font-bold tracking-tight">{t("Create")}</h1>
      <p className="mt-1 text-ink-faint">
        {t("Study your own PDF, or generate a tailored question set to practise.")}
      </p>

      {/* Generating now — elevated to the top so a just-started run is the first
          thing seen after the redirect from "Generate". Shows optimistic
          placeholder pills (POST still in flight) ahead of the live runs the
          server already knows about. Polls every 4s. */}
      {(activeRuns.length > 0 || pendingRuns.length > 0) && (
        <section className="mt-6">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
            {t("Generating now")}
          </h2>
          <p className="mt-1 text-sm text-ink-soft">
            {t("Your set is being built — it'll appear in your Library when it's ready. You can leave this page; we'll keep working.")}
          </p>
          <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {pendingRuns.map((pr) => {
              const p = presentationFor(pr.frameworkSlug);
              const failed = pr.status === "failed";
              return (
                <div
                  key={pr.placeholderId}
                  className={
                    "card flex items-center gap-3 p-3 " +
                    (failed ? "border-rose-200 bg-rose-50/40" : "")
                  }
                >
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xl ${p.tile}`}>
                    {p.emoji}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{pr.frameworkTitle}</p>
                    {failed ? (
                      <p className="text-xs font-medium text-rose-700">
                        {generationErrorMessage(pr.error, t)}
                      </p>
                    ) : (
                      <p className="flex items-center gap-1.5 text-xs font-medium text-brand-600">
                        <Spinner className="h-3 w-3" />
                        {t("Generating…")}
                      </p>
                    )}
                  </div>
                  {failed && (
                    <button
                      type="button"
                      onClick={() => dismissRun(pr.placeholderId)}
                      aria-label={t("Dismiss")}
                      className="shrink-0 text-ink-faint hover:text-rose-600"
                    >
                      ✕
                    </button>
                  )}
                </div>
              );
            })}
            {activeRuns.map((r) => {
              const p = presentationFor(r.framework_slug);
              const awaiting = r.status === "awaiting_user";
              return (
                <Link
                  key={r.id}
                  to={`/app/create/run/${r.id}`}
                  className="card flex items-center gap-3 p-3 transition hover:-translate-y-0.5 hover:shadow-glow"
                >
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xl ${p.tile}`}>
                    {p.emoji}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{titleFor(r.framework_slug)}</p>
                    <p
                      className={
                        "flex items-center gap-1.5 text-xs font-medium " +
                        (awaiting ? "text-amber-700" : "text-brand-600")
                      }
                    >
                      {!awaiting && <Spinner className="h-3 w-3" />}
                      {awaiting ? t("Awaiting you →") : t("Generating…")}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Continue learning — languages the user is mid-course on; one tap starts
          the next adaptive language-trainer set. Renders nothing if none. */}
      <ContinueLearning />

      {/* Projects — the deep workbench. Steers coursework/assignment-shaped work
          here so the framework gallery below reads clearly as the fast-practice
          path (the project→practice seam: a project also produces a trainable
          set, which lands in your Library to drill + earn minutes). */}
      <section className="mt-6">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
          {t("Working through real coursework?")}
        </h2>
        <Link
          to="/app/projects/new"
          className="mt-2 flex items-center gap-4 rounded-2xl border border-violet-300 bg-violet-50 p-5 shadow-soft transition hover:-translate-y-0.5 hover:shadow-glow"
        >
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-2xl">
            📂
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-ink">{t("Start a Project")}</p>
            <p className="text-sm text-ink-soft">
              {t("Bring a folder of material plus an assignment — Eddi reads it, solves the task, and turns it into practice you can drill here.")}
              {" "}<span className="font-medium text-violet-800">{t("→")}</span>
            </p>
          </div>
        </Link>
      </section>

      {/* Study mode — a different kind of action (read & mark, no generation). */}
      <section className="mt-6">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
          {t("Study your own material")}
        </h2>
        <Link
          to="/app/study"
          className="mt-2 flex items-center gap-4 rounded-2xl border border-amber-300 bg-amber-50 p-5 shadow-soft transition hover:-translate-y-0.5 hover:shadow-glow"
        >
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-2xl">
            ✍️
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-ink">{t("Mark & study a PDF")}</p>
            <p className="text-sm text-ink-soft">
              {t("Read your own lecture or paper, mark what matters, and get instant explanations —")}
              <span className="font-medium text-amber-800"> {t("no question set is generated")}</span>.
              {" "}{t("Turn your marks into a set later if you want. →")}
            </p>
          </div>
        </Link>
      </section>

      <section className="mt-8">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
          {t("Generate a question set")}
        </h2>
        <p className="mt-1 text-sm text-ink-soft">
          {t("Pick a topic or paste a source — a focused set to drill and earn minutes, ready in moments.")}
        </p>

        {isLoading && (
          <div className="flex justify-center py-20 text-brand-600">
            <Spinner className="h-7 w-7" />
          </div>
        )}

        {isError && (
          <div className="mt-3">
            <Alert>{error instanceof Error ? error.message : t("Couldn't load frameworks.")}</Alert>
          </div>
        )}

        {data && (
          <div className="mt-2 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {data.map((fw) => {
            const p = presentationFor(fw.slug);
            return (
              <Link
                key={fw.slug}
                to={`/app/create/${encodeURIComponent(fw.slug)}`}
                className="card group flex flex-col p-5 transition hover:-translate-y-0.5 hover:shadow-glow"
              >
                <div className={`flex h-12 w-12 items-center justify-center rounded-xl text-2xl ${p.tile}`}>
                  {p.emoji}
                </div>
                <h2 className="mt-4 font-semibold text-ink group-hover:text-brand-700">{fw.title}</h2>
                <p className="mt-1 flex-1 text-sm text-ink-faint">{p.tagline ? t(p.tagline) : fw.description}</p>
                {fw.cost_estimate?.expected_questions != null && (
                  <p className="mt-3 text-xs font-medium text-ink-faint">
                    {t("~{{count}} questions", { count: fw.cost_estimate.expected_questions })}
                  </p>
                )}
              </Link>
            );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
